import React, { useState, useEffect } from 'react';
import { X, Shield } from 'lucide-react';
import { Button } from './button';
import { Link } from 'react-router-dom';

// Banner height constant for consistent spacing
const BANNER_HEIGHT = '52px'; // This matches our py-3 (padding-top + padding-bottom = 0.75rem * 2 = 1.5rem = 24px) + content height

interface AnnouncementBannerProps {
  message: React.ReactNode;
  backgroundColor?: string;
  textColor?: string;
  ctaText?: string;
  ctaLink?: string;
  isInternalLink?: boolean;
  announcementId?: string;
  showIcon?: boolean;
  onClose?: () => void;
}

const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({
  message,
  backgroundColor = 'bg-blue-50',
  textColor = 'text-blue-900',
  ctaText,
  ctaLink,
  isInternalLink = false,
  announcementId = 'default',
  showIcon = false,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const dismissedAnnouncement = sessionStorage.getItem(`announcement-${announcementId}`);
      if (!dismissedAnnouncement) {
        const timer = setTimeout(() => {
          setIsVisible(true);
          document.body.classList.add('has-announcement');
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [announcementId]);

  const handleDismiss = () => {
    setIsVisible(false);
    document.body.classList.remove('has-announcement');
    
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(`announcement-${announcementId}`, 'dismissed');
    }
    
    if (onClose) {
      onClose();
    }
  };

  if (!isVisible) {
    return null;
  }

  const hasHexColor = backgroundColor.startsWith('#');
  const hasHexTextColor = textColor.startsWith('#');

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed top-0 left-0 right-0 w-full transform transition-all duration-300 ease-in-out z-[9990]"
      style={{
        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
        height: 'var(--announcement-height)',
      }}
    >
      {/* Gradient overlay container */}
      <div 
        className="relative overflow-hidden h-full"
        style={{
          backgroundColor: hasHexColor ? backgroundColor : undefined,
        }}
      >
        {/* Subtle gradient overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            background: 'linear-gradient(45deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 100%)'
          }}
        />

        {/* Content container */}
        <div className="max-w-7xl mx-auto relative h-full">
          <div className="px-6 sm:px-8 lg:px-10 h-full flex items-center">
            <div className="flex items-center justify-between w-full">
              <div className="flex-grow flex items-center justify-center gap-4">
                {showIcon && (
                  <Shield 
                    className="h-[22px] w-[22px] flex-shrink-0 transition-transform duration-200 hover:scale-110" 
                    style={{ color: hasHexTextColor ? textColor : undefined }} 
                    aria-hidden="true" 
                  />
                )}
                <p 
                  className="text-base font-semibold tracking-wide py-0.5"
                  style={{ color: hasHexTextColor ? textColor : undefined }}
                >
                  {message}
                </p>
                {ctaText && ctaLink && (
                  <>
                    <span 
                      className="mx-2 opacity-75"
                      style={{ color: hasHexTextColor ? textColor : undefined }}
                    >
                      •
                    </span>
                    {isInternalLink ? (
                      <Link
                        to={ctaLink}
                        className="text-base font-bold hover:opacity-90 transition-opacity duration-200"
                        style={{ color: hasHexTextColor ? textColor : undefined }}
                        aria-label={`${ctaText} - opens in current tab`}
                      >
                        {ctaText}
                      </Link>
                    ) : (
                      <a
                        href={ctaLink}
                        className="text-base font-bold hover:opacity-90 transition-opacity duration-200"
                        style={{ color: hasHexTextColor ? textColor : undefined }}
                        aria-label={`${ctaText} - opens in current tab`}
                      >
                        {ctaText}
                      </a>
                    )}
                  </>
                )}
              </div>
              <button
                onClick={handleDismiss}
                className="flex-shrink-0 -mr-2 p-2.5 rounded-full transition-all duration-200 hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-white/20 group"
                aria-label="Dismiss announcement"
              >
                <X 
                  className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" 
                  style={{ color: hasHexTextColor ? textColor : undefined }} 
                  aria-hidden="true" 
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner; 